import { type ReactNode, useState } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { Dialog, DialogContent, Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./OfferDetailsDialog.module.scss";

/** BEM helper bound to the dialog block, handed to slot renderers. */
export type OfferDetailsDialogCn = (
  element: string,
  modifiers?: string[]
) => string;

export interface OfferDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Accessible dialog title (visually hidden). */
  title: string;
  /** Omitted → no hero strip. */
  heroImageSrc?: string;
  /** Left column: heading, badge, valid-until, details sections. */
  details: (cn: OfferDetailsDialogCn) => ReactNode;
  /** Right column card body ("How it works"). */
  howItWorks: (cn: OfferDetailsDialogCn) => ReactNode;
  /** Optional row under the card (status + CTA). */
  ctaRow?: (cn: OfferDetailsDialogCn) => ReactNode;
  /** Expandable terms strip; omitted when undefined. */
  terms?: {
    label: string;
    content: (cn: OfferDetailsDialogCn) => ReactNode;
  };
  className?: string;
}

/**
 * Shared offer dialog layout (Figma 36018-138836 / 2011-94570): hero image,
 * details + "How it works" columns, optional terms accordion.
 */
export const OfferDetailsDialog = ({
  open,
  onOpenChange,
  title,
  heroImageSrc,
  details,
  howItWorks,
  ctaRow,
  terms,
  className,
}: OfferDetailsDialogProps) => {
  const [block, cn] = useModuleBEM(styles, "OfferDetailsDialog");
  const [termsOpen, setTermsOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={clsx(block, className)}
        title={title}
        titleIsVisuallyHidden
      >
        {heroImageSrc != null && (
          <img className={cn("image")} src={heroImageSrc} alt="" aria-hidden />
        )}
        <div className={cn("body")}>
          <div className={cn("details")}>{details(cn)}</div>
          <div className={cn("how-it-works")}>
            <div className={cn("how-it-works-card")}>{howItWorks(cn)}</div>
            {ctaRow != null && (
              <div className={cn("cta-row")}>{ctaRow(cn)}</div>
            )}
          </div>
        </div>
        {terms != null && (
          <div className={cn("terms")}>
            <AccordionPrimitive.Root
              type="single"
              collapsible
              onValueChange={(value) => setTermsOpen(value === "terms")}
            >
              <AccordionPrimitive.Item value="terms">
                <AccordionPrimitive.Header>
                  <AccordionPrimitive.Trigger className={cn("terms-trigger")}>
                    <Icon
                      iconName={
                        termsOpen ? IconNameEnum.minus : IconNameEnum.plus
                      }
                      aria-hidden
                    />
                    {terms.label}
                  </AccordionPrimitive.Trigger>
                </AccordionPrimitive.Header>
                <AccordionPrimitive.Content className={cn("terms-content")}>
                  {terms.content(cn)}
                </AccordionPrimitive.Content>
              </AccordionPrimitive.Item>
            </AccordionPrimitive.Root>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
